// frontend/src/modules/admin/components/GestionClients.jsx
// ✅ UPGRADE 20/12/2025: Ajout stats réservations (validées, annulées, total €)
// ✅ NOUVEAU: Modal détail client avec historique réservations
// Papy 73+

import React, { useState, useEffect } from 'react';
import { 
  Users, Plus, Edit, Trash2, Mail, Phone, Search, X, Printer, Send, 
  Newspaper, Image, Video, Eye, Link, Upload, RefreshCw,
  ShoppingCart, CheckCircle, XCircle, Euro, Calendar, ChevronDown, ChevronUp,
  FileText, Clock
} from 'lucide-react';
// ✅ Import MessageBox - Ajuster le chemin selon l'emplacement du fichier
import { useMessageBox } from '../../../components/MessageBox';

const GestionClients = () => {
  // ✅ Hook MessageBox
  const { showMessage, showConfirm } = useMessageBox();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    nom_prenom: '',
    telephone: '',
    email: '',
    code_client: '',
    qrcode: '',
    user_status: 'actif',
    valide_rgpd: 'oui',       
    accord_promotion: 'non'    
  });

  const [showNewsletterModal, setShowNewsletterModal] = useState(false);
  const [newsletterData, setNewsletterData] = useState({
    sujet: '',
    contenu: '',
    imageUrl: '',
    videoUrl: ''
  });
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoType, setVideoType] = useState('none');
  const [existingVideos, setExistingVideos] = useState([]);

  // ✅ NOUVEAU: États pour modal détail client
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailClient, setDetailClient] = useState(null);
  const [clientReservations, setClientReservations] = useState([]);
  const [loadingReservations, setLoadingReservations] = useState(false);
  const [expandedReservation, setExpandedReservation] = useState(null);

  // Charger les clients depuis l'API (avec stats)
  const loadClients = async () => {
    try {
      const response = await fetch('/api/clients?with_stats=1');
      const data = await response.json();
      
      if (data.success) {
        setClients(data.data);
      } else {
        console.error('Erreur récupération clients:', data.message);
      }
    } catch (error) {
      console.error('Erreur réseau:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  // ✅ NOUVEAU: Charger les réservations d'un client
  const loadClientReservations = async (codeClient) => {
    setLoadingReservations(true);
    try {
      const response = await fetch(`/api/reservations?code_client=${codeClient}`);
      const data = await response.json();
      
      if (data.success) {
        setClientReservations(data.data || []);
      } else {
        setClientReservations([]);
      }
    } catch (error) {
      console.error('Erreur chargement réservations:', error);
      setClientReservations([]);
    } finally {
      setLoadingReservations(false);
    }
  };

  // ✅ NOUVEAU: Ouvrir le modal détail client
  const handleViewDetails = async (client) => {
    setDetailClient(client);
    setShowDetailModal(true);
    setExpandedReservation(null);
    await loadClientReservations(client.code_client);
  };

  // Réinitialiser le formulaire
  const resetForm = () => {
    setFormData({
      nom_prenom: '',
      telephone: '',
      email: '',
      code_client: '',
      qrcode: '',
      user_status: 'actif',
      valide_rgpd: 'oui',
      accord_promotion: 'non'
    });
    setSelectedClient(null);
  };

  // Ouvrir modal création
  const handleCreate = () => {
    resetForm();
    setModalMode('create');
    setShowModal(true);
  };

  // Ouvrir modal édition
  const handleEdit = (client) => {
    setFormData({
      nom_prenom: client.nom_prenom || '',
      telephone: client.telephone || '',
      email: client.email || '',
      code_client: client.code_client || '',
      qrcode: client.qrcode || '',                 
      user_status: client.user_status || 'actif',  
      valide_rgpd: client.valide_rgpd || 'non',
      accord_promotion: client.accord_promotion || 'non'
    });
    setSelectedClient(client);
    setModalMode('edit');
    setShowModal(true);
  };

  // 🖨️ FONCTION IMPRESSION BADGE CLIENT
  const handlePrintBadge = async (client) => {
    try {
      const response = await fetch(
        `/api/print/badge-qr-client?code_client=${client.code_client}`
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `badge_${client.nom_prenom.replace(/ /g, '_')}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        showMessage('success', `Badge généré pour ${client.nom_prenom}`);
      } else {
        const errorData = await response.json();
        showMessage('error', `Erreur génération badge: ${errorData.message || 'Erreur inconnue'}`);
      }
    } catch (error) {
      showMessage('error', `Erreur réseau: ${error.message}`);
    }
  };

  // Renvoyer email bienvenue
  const handleResendWelcome = async (client) => {
    const confirmResult = await showConfirm(
      `Renvoyer l'email de bienvenue à ${client.nom_prenom} ?\n\nUn nouveau mot de passe sera généré.`,
      'warning'
    );
    if (!confirmResult) return;

    try {
      const response = await fetch(`/api/clients/resend-welcome/${client.id}`, {
        method: 'POST'
      });
      const data = await response.json();

      if (data.success) {
        showMessage('success', `Email renvoyé à ${client.email}`);
      } else {
        showMessage('error', data.message || 'Erreur envoi email');
      }
    } catch (error) {
      showMessage('error', `Erreur: ${error.message}`);
    }
  };

  // Extraire l'ID YouTube d'une URL
  const extractYouTubeId = (url) => {
    if (!url) return null;
    const regex = /(?:youtube\.com\/(?:shorts\/|watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  // Upload image vers le serveur
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showMessage('error', 'Type de fichier non autorisé. Formats acceptés: JPG, PNG, GIF, WEBP');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showMessage('error', 'Fichier trop volumineux (max 5 Mo)');
      return;
    }

    setUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch('/api/clients/upload/newsletter-image', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setNewsletterData({ ...newsletterData, imageUrl: data.url });
        showMessage('success', `Image uploadée: ${data.filename}`);
      } else {
        showMessage('error', data.message || 'Erreur upload');
      }
    } catch (error) {
      showMessage('error', `Erreur: ${error.message}`);
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  // Upload vidéo locale
  const handleVideoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    if (!allowedTypes.includes(file.type)) {
      showMessage('error', 'Format vidéo non supporté. Utilisez MP4 ou WEBM.');
      return;
    }

    if (file.size > 100 * 1024 * 1024) {
      showMessage('error', 'Vidéo trop volumineuse (max 100 Mo)');
      return;
    }

    setUploadingVideo(true);

    try {
      const formData = new FormData();
      formData.append('video', file);

      const response = await fetch('/api/clients/upload/newsletter-video', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setNewsletterData({ ...newsletterData, videoUrl: data.url });
        showMessage('success', `Vidéo uploadée: ${data.filename}`);
      } else {
        showMessage('error', data.message || 'Erreur upload');
      }
    } catch (error) {
      showMessage('error', `Erreur: ${error.message}`);
    } finally {
      setUploadingVideo(false);
      event.target.value = '';
    }
  };

  // Charger vidéos existantes
  const loadExistingVideos = async () => {
    try {
      const response = await fetch('/api/clients/upload/newsletter-videos');
      const data = await response.json();
      if (data.success) {
        setExistingVideos(data.videos || []);
      }
    } catch (error) {
      console.error('Erreur chargement vidéos:', error);
    }
  };

  // Envoyer newsletter
  const handleSendNewsletter = async () => {
    const abonnes = clients.filter(c => c.accord_promotion === 'oui');
    
    if (abonnes.length === 0) {
      showMessage('warning', 'Aucun client abonné à la newsletter');
      return;
    }

    const confirmResult = await showConfirm(
      `Envoyer la newsletter à ${abonnes.length} client(s) ?`,
      'warning'
    );
    if (!confirmResult) return;

    setSendingNewsletter(true);

    try {
      const response = await fetch('/api/clients/send-newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newsletterData)
      });

      const data = await response.json();

      if (data.success) {
        showMessage('success', `Newsletter envoyée à ${data.sent} client(s) !`);
        setShowNewsletterModal(false);
        setNewsletterData({ sujet: '', contenu: '', imageUrl: '', videoUrl: '' });
      } else {
        showMessage('error', data.message || 'Erreur envoi newsletter');
      }
    } catch (error) {
      showMessage('error', `Erreur: ${error.message}`);
    } finally {
      setSendingNewsletter(false);
    }
  };

  // Soumettre le formulaire (création/modification)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nom_prenom || !formData.email) {
      showMessage('error', 'Le nom et l\'email sont obligatoires');
      return;
    }

    try {
      const url = modalMode === 'create' 
        ? '/api/clients' 
        : `/api/clients/${selectedClient.id}`;
      
      const method = modalMode === 'create' ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        await loadClients();
        setShowModal(false);
        resetForm();

        if (modalMode === 'create') {
          showMessage('success', `Client créé ! Mot de passe: ${data.data?.motDePasse || '(voir email)'}`);
        } else {
          showMessage('success', 'Client modifié avec succès !');
        }
      } else {
        showMessage('error', `Erreur: ${data.message}`);
      }
    } catch (error) {
      showMessage('error', `Erreur réseau: ${error.message}`);
    }
  };

  // Supprimer client
  const handleDelete = async (client) => {
    const confirmResult = await showConfirm(`Supprimer le client "${client.nom_prenom}" ?`, 'warning');
    if (!confirmResult) return;

    try {
      const response = await fetch(`/api/clients/${client.id}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        await loadClients();
        showMessage('success', 'Client supprimé avec succès !');
      } else {
        showMessage('error', `Erreur: ${data.message}`);
      }
    } catch (error) {
      showMessage('error', `Erreur réseau: ${error.message}`);
    }
  };

  // Filtrer les clients selon le terme de recherche
  const clientsFiltres = clients.filter(client => {
    const search = searchTerm.toLowerCase();
    return (
      client.nom_prenom?.toLowerCase().includes(search) ||
      client.email?.toLowerCase().includes(search) ||
      client.code_client?.includes(searchTerm) ||
      client.telephone?.includes(searchTerm)
    );
  });

  // ✅ NOUVEAU: Formater le statut de réservation
  const getStatutBadge = (statut) => {
    const statutConfig = {
      'en_cours': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'En cours' },
      'confirmee': { bg: 'bg-green-100', text: 'text-green-800', label: 'Confirmée' },
      'prete': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Prête' },
      'livree': { bg: 'bg-green-200', text: 'text-green-900', label: 'Livrée' },
      'annulee': { bg: 'bg-red-100', text: 'text-red-800', label: 'Annulée' },
      'en_preparation': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'En préparation' }
    };
    const config = statutConfig[statut] || { bg: 'bg-gray-100', text: 'text-gray-800', label: statut };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  // ✅ NOUVEAU: Formater la date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Chargement des clients...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-green-800">Gestion des Clients</h2>
        <div className="flex gap-3">
          <button
            onClick={() => setShowNewsletterModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
          >
            <Newspaper className="w-5 h-5" />
            Newsletter
            <span className="bg-blue-800 text-xs px-2 py-1 rounded-full">
              {clients.filter(c => c.accord_promotion === 'oui').length}
            </span>
          </button>
          <button
            onClick={handleCreate}
            className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700"
          >
            <Plus className="w-5 h-5" />
            Nouveau Client
          </button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-100 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-600 text-sm">Total Clients</p>
              <p className="text-2xl font-bold text-blue-900">{clients.length}</p>
            </div>
            <Users className="w-10 h-10 text-blue-500" />
          </div>
        </div>
        <div className="bg-green-100 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-600 text-sm">Actifs</p>
              <p className="text-2xl font-bold text-green-900">
                {clients.filter(c => c.user_status === 'actif').length}
              </p>
            </div>
            <Users className="w-10 h-10 text-green-500" />
          </div>
        </div>
        <div className="bg-purple-100 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-600 text-sm">Newsletter</p>
              <p className="text-2xl font-bold text-purple-900">
                {clients.filter(c => c.accord_promotion === 'oui').length}
              </p>
            </div>
            <Mail className="w-10 h-10 text-purple-500" />
          </div>
        </div>
        <div className="bg-yellow-100 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-600 text-sm">CA Total Clients</p>
              <p className="text-2xl font-bold text-yellow-900">
                {clients.reduce((sum, c) => sum + (parseFloat(c.total_depense) || 0), 0).toFixed(0)}€
              </p>
            </div>
            <Euro className="w-10 h-10 text-yellow-500" />
          </div>
        </div>
      </div>

      {/* Recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          placeholder="Rechercher par nom, email, code ou téléphone..."
          className="w-full pl-10 pr-4 py-2 border rounded-lg"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2"
          >
            <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {/* Liste des clients */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  <span className="flex items-center justify-center gap-1">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Validées
                  </span>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  <span className="flex items-center justify-center gap-1">
                    <XCircle className="w-4 h-4 text-red-600" />
                    Annulées
                  </span>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  <span className="flex items-center justify-center gap-1">
                    <Euro className="w-4 h-4 text-yellow-600" />
                    Total €
                  </span>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {clientsFiltres.map((client) => (
                <tr key={client.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{client.nom_prenom}</div>
                    <code className="text-xs text-gray-500">{client.code_client}</code>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Mail className="w-4 h-4" />
                      {client.email}
                    </div>
                    {client.telephone && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                        <Phone className="w-4 h-4" />
                        {client.telephone}
                      </div>
                    )}
                  </td>
                  {/* ✅ NOUVEAU: Colonnes stats réservations */}
                  <td className="px-4 py-4 whitespace-nowrap text-center">
                    <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm font-medium">
                      <CheckCircle className="w-4 h-4" />
                      {client.nb_reservations_validees || 0}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-center">
                    <span className="inline-flex items-center gap-1 bg-red-100 text-red-800 px-2 py-1 rounded-full text-sm font-medium">
                      <XCircle className="w-4 h-4" />
                      {client.nb_reservations_annulees || 0}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-center">
                    <span className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-sm font-medium">
                      <Euro className="w-4 h-4" />
                      {parseFloat(client.total_depense || 0).toFixed(2)}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-center">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      client.user_status === 'actif' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {client.user_status === 'actif' ? 'Actif' : client.user_status === 'inactif' ? 'Inactif' : 'Bloqué'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right">
                    <div className="flex justify-end gap-1">
                      {/* ✅ NOUVEAU: Bouton voir détails */}
                      <button
                        onClick={() => handleViewDetails(client)}
                        className="p-2 text-indigo-600 hover:bg-indigo-50 rounded"
                        title="Voir historique"
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handlePrintBadge(client)}
                        className="p-2 text-purple-600 hover:bg-purple-50 rounded"
                        title="Imprimer badge QR"
                      >
                        <Printer className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleResendWelcome(client)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title="Renvoyer email bienvenue"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(client)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded"
                        title="Modifier"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(client)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {clientsFiltres.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            {searchTerm ? 'Aucun client ne correspond à la recherche' : 'Aucun client enregistré'}
          </div>
        )}
      </div>

      {/* ✅ NOUVEAU: MODAL DÉTAIL CLIENT */}
      {showDetailModal && detailClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold">{detailClient.nom_prenom}</h3>
                  <p className="text-indigo-200 mt-1">{detailClient.email}</p>
                  <code className="text-indigo-300 text-sm">{detailClient.code_client}</code>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-white hover:bg-indigo-800 p-2 rounded-full"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {/* Stats résumées */}
              <div className="grid grid-cols-3 gap-4 mt-6">
                <div className="bg-white/20 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    <span className="text-2xl font-bold">{detailClient.nb_reservations_validees || 0}</span>
                  </div>
                  <p className="text-sm text-indigo-200">Validées</p>
                </div>
                <div className="bg-white/20 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <XCircle className="w-5 h-5" />
                    <span className="text-2xl font-bold">{detailClient.nb_reservations_annulees || 0}</span>
                  </div>
                  <p className="text-sm text-indigo-200">Annulées</p>
                </div>
                <div className="bg-white/20 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Euro className="w-5 h-5" />
                    <span className="text-2xl font-bold">{parseFloat(detailClient.total_depense || 0).toFixed(0)}€</span>
                  </div>
                  <p className="text-sm text-indigo-200">Total dépensé</p>
                </div>
              </div>
            </div>

            {/* Liste des réservations */}
            <div className="flex-1 overflow-y-auto p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Historique des réservations
              </h4>

              {loadingReservations ? (
                <div className="flex justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
                </div>
              ) : clientReservations.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucune réservation pour ce client</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {clientReservations.map((resa) => (
                    <div 
                      key={resa.id} 
                      className="border rounded-lg overflow-hidden"
                    >
                      {/* Ligne principale */}
                      <div 
                        className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100"
                        onClick={() => setExpandedReservation(expandedReservation === resa.id ? null : resa.id)}
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Calendar className="w-4 h-4" />
                            {formatDate(resa.date_reservation)}
                          </div>
                          <code className="text-sm bg-white px-2 py-1 rounded">{resa.numero_reservation}</code>
                          {getStatutBadge(resa.statut)}
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-bold text-gray-900">
                            {parseFloat(resa.montant_reel || resa.montant_estime || 0).toFixed(2)}€
                          </span>
                          {expandedReservation === resa.id ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>

                      {/* Détails (si expandé) */}
                      {expandedReservation === resa.id && (
                        <div className="p-4 bg-white border-t">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Période:</span>
                              <span className="ml-2 font-medium">{resa.periode_nom || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Date retrait:</span>
                              <span className="ml-2 font-medium">{formatDate(resa.date_retrait_prevue)}</span>
                            </div>
                            {resa.notes_client && (
                              <div className="col-span-2">
                                <span className="text-gray-500">Notes:</span>
                                <span className="ml-2">{resa.notes_client}</span>
                              </div>
                            )}
                          </div>
                          {/* TODO: Ajouter les lignes de la réservation si besoin */}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t p-4 bg-gray-50">
              <button
                onClick={() => setShowDetailModal(false)}
                className="w-full py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CRÉATION/ÉDITION CLIENT */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  {modalMode === 'create' ? 'Nouveau Client' : 'Modifier Client'}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nom */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom et Prénom *
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border rounded-lg"
                    value={formData.nom_prenom}
                    onChange={(e) => setFormData({...formData, nom_prenom: e.target.value})}
                    placeholder="Jean Dupont"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    className="w-full px-3 py-2 border rounded-lg"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="jean.dupont@email.com"
                  />
                </div>

                {/* Téléphone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    className="w-full px-3 py-2 border rounded-lg"
                    value={formData.telephone}
                    onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                    placeholder="06 12 34 56 78"
                  />
                </div>

                {/* Statut */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Statut
                  </label>
                  <select
                    className="w-full px-3 py-2 border rounded-lg"
                    value={formData.user_status}
                    onChange={(e) => setFormData({...formData, user_status: e.target.value})}
                  >
                    <option value="actif">Actif</option>
                    <option value="inactif">Inactif</option>
                    <option value="bloque">Bloqué</option>
                  </select>
                </div>

                {/* RGPD */}
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.valide_rgpd === 'oui'}
                      onChange={(e) => setFormData({...formData, valide_rgpd: e.target.checked ? 'oui' : 'non'})}
                      className="w-4 h-4 text-green-600"
                    />
                    <span className="text-sm text-gray-700">RGPD validé</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.accord_promotion === 'oui'}
                      onChange={(e) => setFormData({...formData, accord_promotion: e.target.checked ? 'oui' : 'non'})}
                      className="w-4 h-4 text-green-600"
                    />
                    <span className="text-sm text-gray-700">Newsletter</span>
                  </label>
                </div>

                {/* Boutons */}
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    {modalMode === 'create' ? 'Créer' : 'Enregistrer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* MODAL NEWSLETTER (simplifié ici, tu gardes ta version complète) */}
      {showNewsletterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Newspaper className="w-6 h-6 text-blue-600" />
                  Envoyer une Newsletter
                </h3>
                <button
                  onClick={() => setShowNewsletterModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Formulaire newsletter */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sujet *</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border rounded-lg"
                    value={newsletterData.sujet}
                    onChange={(e) => setNewsletterData({...newsletterData, sujet: e.target.value})}
                    placeholder="Nouveautés de la ferme !"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                  <textarea
                    className="w-full px-3 py-2 border rounded-lg h-40"
                    value={newsletterData.contenu}
                    onChange={(e) => setNewsletterData({...newsletterData, contenu: e.target.value})}
                    placeholder="Bonjour à tous..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL Image (optionnel)</label>
                  <input
                    type="url"
                    className="w-full px-3 py-2 border rounded-lg"
                    value={newsletterData.imageUrl}
                    onChange={(e) => setNewsletterData({...newsletterData, imageUrl: e.target.value})}
                    placeholder="https://..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL Vidéo YouTube (optionnel)</label>
                  <input
                    type="url"
                    className="w-full px-3 py-2 border rounded-lg"
                    value={newsletterData.videoUrl}
                    onChange={(e) => setNewsletterData({...newsletterData, videoUrl: e.target.value})}
                    placeholder="https://youtube.com/..."
                  />
                </div>
              </div>

              {/* Boutons */}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowNewsletterModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  disabled={sendingNewsletter}
                >
                  Annuler
                </button>
                <button
                  onClick={handleSendNewsletter}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  disabled={sendingNewsletter || !newsletterData.sujet || !newsletterData.contenu}
                >
                  {sendingNewsletter ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Envoyer à {clients.filter(c => c.accord_promotion === 'oui').length} clients
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionClients;